import React from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    displayAccountLabel,
    getShortJournalEntryReferenceNumber,
} from "@common/helpers/accounting";
import { fullDateLongFormat } from "@common/helpers/date";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { formatDateOnly } from "@helpers/date";
import { useEntitiesById } from "@hooks/useEntities";
import { useIdCopy } from "@hooks/useIdCopy";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CheckIcon } from "@icons";
import { useOverlays } from "contexts/OverlayContext";
import { GL_COPY_LABEL } from "../generalLedger/Cells/glCellHelpers";
import generalLedgerStyles from "../generalLedger/GeneralLedgerTable.module.scss";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import { CheckDetailViewRow } from "@common/types/reports/check-detail";

type CheckDetailCellContext = CellContext<
    ViewTableFeatures,
    CheckDetailViewRow,
    string
>;

type CheckTextField = "checkNumber" | "payee" | "description" | "memo";

function checkFieldCell(field: CheckTextField) {
    return React.memo(function CheckFieldCell({
        row,
    }: CheckDetailCellContext): React.ReactElement {
        const { original } = row;
        return (
            <ViewTableCell>
                {original.rowKind === "check" ? original.check[field] : null}
            </ViewTableCell>
        );
    });
}

export const CheckDetailCheckNumberCell = checkFieldCell("checkNumber");
export const CheckDetailPayeeCell = checkFieldCell("payee");
export const CheckDetailDescriptionCell = checkFieldCell("description");
export const CheckDetailMemoCell = checkFieldCell("memo");

export const CheckDetailReferenceCell = React.memo(
    function CheckDetailReferenceCell({
        row,
    }: CheckDetailCellContext): React.ReactElement {
        const { original } = row;
        const entitiesById = useEntitiesById();
        const { openOverlay } = useOverlays();
        const { getCopyProps } = useIdCopy({ label: GL_COPY_LABEL });

        if (original.rowKind !== "check") {
            return <ViewTableCell />;
        }
        const { journalEntryReference, entityId } = original.check;
        const entity = entitiesById.get(entityId);
        if (!journalEntryReference || !entity) {
            return <ViewTableCell />;
        }
        return (
            <ViewTableCell
                captureClicks
                className={generalLedgerStyles.clickableCell}
                onClick={() =>
                    openOverlay("journalEntryDrawer", {
                        entity,
                        journalEntryId: journalEntryReference,
                        ledgerBasis: AccountingLedgerBasis.cash,
                    })
                }
                {...getCopyProps(journalEntryReference)}
            >
                {getShortJournalEntryReferenceNumber(journalEntryReference)}
            </ViewTableCell>
        );
    },
);

export const CheckDetailDateCell = React.memo(function CheckDetailDateCell({
    row,
}: CheckDetailCellContext): React.ReactElement {
    const { original } = row;

    if (original.rowKind !== "check") {
        return (
            <ViewTableCell className={generalLedgerStyles.accountCell}>
                {original.title}
            </ViewTableCell>
        );
    }
    return (
        <ViewTableCell>
            {formatDateOnly(original.check.dateOnly, fullDateLongFormat)}
        </ViewTableCell>
    );
});

export const CheckDetailEntityCell = React.memo(function CheckDetailEntityCell({
    row,
}: CheckDetailCellContext): React.ReactElement {
    const { original } = row;
    const entitiesById = useEntitiesById();
    const entity =
        original.rowKind === "check"
            ? entitiesById.get(original.check.entityId)
            : undefined;

    if (!entity) {
        return <ViewTableCell />;
    }
    return (
        <ViewTableCell>
            <EntityAvatarGroup entities={[entity]} max={3} size="xs" />
        </ViewTableCell>
    );
});

export const CheckDetailClassCell = React.memo(function CheckDetailClassCell({
    row,
}: CheckDetailCellContext): React.ReactElement {
    const { original } = row;

    if (
        original.rowKind !== "check" ||
        original.check.classNames.length === 0
    ) {
        return <ViewTableCell />;
    }
    return (
        <ViewTableCell>{original.check.classNames.join(", ")}</ViewTableCell>
    );
});

export const CheckDetailAccountCell = React.memo(
    function CheckDetailAccountCell({
        row,
    }: CheckDetailCellContext): React.ReactElement {
        const showAccountNumbers = useShowAccountNumbers();
        const { original } = row;

        if (original.rowKind !== "check") {
            return <ViewTableCell />;
        }
        return (
            <ViewTableCell>
                {displayAccountLabel(
                    {
                        name: original.check.accountName,
                        codeText: original.check.accountCodeText,
                    },
                    showAccountNumbers,
                )}
            </ViewTableCell>
        );
    },
);

export const CheckDetailExcludeFrom1099Cell = React.memo(
    function CheckDetailExcludeFrom1099Cell({
        row,
    }: CheckDetailCellContext): React.ReactElement {
        const { original } = row;

        if (original.rowKind !== "check") {
            return <ViewTableCell />;
        }
        return (
            <ViewTableCell className={generalLedgerStyles.centeredCell}>
                {original.check.excludeFrom1099 ? "Yes" : "No"}
            </ViewTableCell>
        );
    },
);

export const CheckDetailClearedDateCell = React.memo(
    function CheckDetailClearedDateCell({
        row,
    }: CheckDetailCellContext): React.ReactElement {
        const { original } = row;

        if (original.rowKind !== "check" || !original.check.clearedDateOnly) {
            return <ViewTableCell />;
        }
        return (
            <ViewTableCell>
                {formatDateOnly(
                    original.check.clearedDateOnly,
                    fullDateLongFormat,
                )}
            </ViewTableCell>
        );
    },
);

export const CheckDetailReconciledCell = React.memo(
    function CheckDetailReconciledCell({
        row,
    }: CheckDetailCellContext): React.ReactElement {
        const { original } = row;

        if (original.rowKind !== "check" || !original.check.isReconciled) {
            return <ViewTableCell />;
        }
        return (
            <ViewTableCell className={generalLedgerStyles.centeredCell}>
                <span role="img" aria-label="Reconciled">
                    <CheckIcon className="icon-color-current" aria-hidden />
                </span>
            </ViewTableCell>
        );
    },
);

export const CheckDetailAmountCell = React.memo(function CheckDetailAmountCell({
    row,
}: CheckDetailCellContext): React.ReactElement {
    const { original } = row;
    const { text, negativeClassName } = useReportAmountDisplay(
        original.rowKind === "check" ? original.check.amount : original.total,
    );

    return (
        <ViewTableCell
            className={classNames(
                generalLedgerStyles.numericCell,
                original.rowKind !== "check" && generalLedgerStyles.accountCell,
                negativeClassName,
            )}
        >
            {text}
        </ViewTableCell>
    );
});
